/*
Copyright 2025 New Vector Ltd.
Copyright 2020 Bruno Windels <bruno@windels.cloud>

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE files in the repository root for full details.
*/

import {TemplateView} from "../../general/TemplateView";
import {Popup} from "../../general/Popup.js";
import {Menu} from "../../general/Menu.js";

export class MessageComposer extends TemplateView {
    constructor(viewModel, viewClassForTile) {
        super(viewModel);
        this._viewClassForTile = viewClassForTile;
        this._input = null;
        this._attachmentPopup = null;
        this._focusInput = null;
        this._rafResizeHandle = undefined;
    }

    render(t, vm) {
        this._input = t.textarea({
            onKeydown: e => this._onKeyDown(e),
            onInput: () => {
                vm.setInput(this._input.value);
                if (this._input.value) {
                    this._adjustHeight();
                } else {
                    this._clearHeight();
                }
            },
            placeholder: vm => vm.isEncrypted ? "Senden Sie eine verschlüsselte Nachricht…" : "Eine Nachricht schicken…",
            rows: "1"
        });
        this._focusInput = () => this._input.focus();
        this.value.on("focus", this._focusInput);
        const replyPreview = t.map(vm => vm.replyViewModel, (rvm, t) => {
            const TileView = rvm && this._viewClassForTile(rvm);
            if (!TileView) { return null; }
            return t.div({
                    className: "MessageComposer_replyPreview"
                }, [
                    t.span({ className: "replying" }, "Replying"),
                    t.button({
                        className: "cancel",
                        onClick: () => this._clearReplyingTo()
                    }, "Close"),
                t.view(new TileView(rvm, this._viewClassForTile, { interactive: false }))
            ]);
        });
        const input = t.div({className: "MessageComposer_input"}, [
            this._input,
            t.button({
                className: "sendFile",
                title: vm.i18n`Anhang auswählen`,
                onClick: evt => this._toggleAttachmentMenu(evt),
            }, vm.i18n`Datei senden`),
            t.button({
                className: "send",
                title: vm.i18n`Senden`,
                onClick: () => this._trySend(),
            }, vm.i18n`Senden`),
        ]);
        return t.div({ className: {
            MessageComposer: true,
            MessageComposer_canSend: vm => vm.canSend
        } }, [replyPreview, input]);
    }

    unmount() {
        if (this._focusInput) {
            this.value.off("focus", this._focusInput);
        }
        super.unmount();
    }

    _clearReplyingTo() {
        this.value.clearReplyingTo();
    }

    async _trySend() {
        this._input.focus();
        // we clear the composer while enqueuing
        // and restore it when that didn't work somehow
        // to prevent the user from sending the message
        // every time they hit enter while it's still enqueuing.
        const {value} = this._input;
        const restoreValue = () => {
            this._input.value = value;
            this._adjustHeight();
        };
        this._input.value = "";
        this._clearHeight();
        try {
            if (!await this.value.sendMessage(value)) {
                restoreValue();
            }
        } catch (err) {
            restoreValue();
            console.error(err);
        }
    }

    _onKeyDown(event) {
        if (event.key === "Enter" && !event.shiftKey) {
            // don't insert newline into composer
            event.preventDefault();
            this._trySend();
        }
    }

    _toggleAttachmentMenu(evt) {
        if (this._attachmentPopup && this._attachmentPopup.isOpen) {
            this._attachmentPopup.close();
        } else {
            const vm = this.value;
            this._attachmentPopup = new Popup(new Menu([
                Menu.option(vm.i18n`Video senden`, () => vm.sendVideo()).setIcon("video"),
                Menu.option(vm.i18n`Bild senden`, () => vm.sendPicture()).setIcon("picture"),
                Menu.option(vm.i18n`Datei senden`, () => vm.sendFile()).setIcon("file"),
            ]));
            this._attachmentPopup.trackInTemplateView(this);
            this._attachmentPopup.showRelativeTo(evt.target, 12);
        }
    }

    _adjustHeight() {
        if (this._rafResizeHandle) {
            return;
        }
        this._rafResizeHandle = window.requestAnimationFrame(() => {
            const scrollHeight = this._input.scrollHeight;
            this._input.style.height = `${scrollHeight}px`;
            this._rafResizeHandle = undefined;
        });
    }

    _clearHeight() {
        this._input.style.removeProperty("height");
    }

}
